import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CiCircleChevDown, CiCircleChevUp } from 'react-icons/ci'
import { FaPlay, FaBuilding, FaCalendarAlt, FaUserTie } from 'react-icons/fa'
import PropTypes from 'prop-types'

const CompanyProjectCard = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [videoPlaying, setVideoPlaying] = useState(false)

  const thumbnailUrl = `https://img.youtube.com/vi/${data.videoId}/maxresdefault.jpg`

  return (
    <motion.article
      className="company__card"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      whileHover={{ y: -6, transition: { duration: 0.3 } }}
    >
      {/* VIDEO */}
      <div className="company__card-video">
        <AnimatePresence mode="wait">
          {videoPlaying ? (
            <motion.div
              key="iframe"
              className="company__card-iframe-wrapper"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <iframe
                src={`https://www.youtube.com/embed/${data.videoId}?autoplay=1&rel=0&modestbranding=1`}
                title={data.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </motion.div>
          ) : (
            <motion.div
              key="thumbnail"
              className="company__card-thumb"
              onClick={() => setVideoPlaying(true)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <img src={thumbnailUrl} alt={data.title} />
              <div className="company__card-overlay">
                <motion.div
                  className="company__card-play"
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaPlay />
                </motion.div>
                <span className="company__card-play-label">Watch Demo</span>
              </div>
              {data.duration && (
                <span className="company__card-duration">{data.duration}</span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* BODY */}
      <div className="company__card-body">

        {/* Meta */}
        <div className="company__card-header">
          <div className="company__card-meta">
            <span className="company__card-company">
              <FaBuilding /> {data.company}
            </span>
            {data.year && (
              <span className="company__card-year">
                <FaCalendarAlt /> {data.year}
              </span>
            )}
          </div>
          {data.role && (
            <span className="company__card-role">
              <FaUserTie /> {data.role}
            </span>
          )}
        </div>

        {/* Title + Toggle */}
        <div className="company__card-title-row">
          <h3 className="company__card-title">{data.title}</h3>
          <motion.button
            className="company__card-toggle"
            onClick={() => setIsExpanded(!isExpanded)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>{isExpanded ? <CiCircleChevUp /> : <CiCircleChevDown />}</span>
            <span>{isExpanded ? 'Less' : 'More'}</span>
          </motion.button>
        </div>

        {/* Short description */}
        <p className="company__card-short-desc">{data.shortDescription}</p>

        {/* Expandable */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              className="company__card-expanded"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
              {data.description && (
                <div className="company__card-desc">
                  <p>{data.description}</p>
                </div>
              )}

              {data.myContribution && (
                <div className="company__card-contribution">
                  <h4>My Contribution</h4>
                  <ul>
                    {data.myContribution.map((item, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: -15 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.08 }}
                      >
                        <span className="company__bullet" />
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              )}

              {data.tools_tech && data.tools_tech.length > 0 && (
                <div className="company__card-tools">
                  <h4>Tech Stack</h4>
                  <div className="company__card-tools-list">
                    {data.tools_tech.map((tool, i) => (
                      <motion.span
                        key={tool}
                        className="company__tag"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.06 }}
                      >
                        {tool}
                      </motion.span>
                    ))}
                  </div>
                </div>
              )}

              {data.impact && (
                <div className="company__card-impact">
                  <h4>Impact</h4>
                  <p>{data.impact}</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer */}
        <div className="company__card-footer">
          <span className="company__nda-badge">🔒 NDA Protected</span>
        </div>
      </div>
    </motion.article>
  )
}

CompanyProjectCard.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    company: PropTypes.string.isRequired,
    year: PropTypes.string,
    role: PropTypes.string,
    videoId: PropTypes.string.isRequired,
    duration: PropTypes.string,
    shortDescription: PropTypes.string.isRequired,
    description: PropTypes.string,
    myContribution: PropTypes.arrayOf(PropTypes.string),
    tools_tech: PropTypes.arrayOf(PropTypes.string),
    impact: PropTypes.string,
  }).isRequired
}
export default CompanyProjectCard